import {
  CliError, DELIVERY_VALUES, EFFECTIVE_FROM_VALUES,
  EXIT_INPUT, EXIT_USAGE, SCRIPT_SCHEMA_VERSION,
  SCRIPT_TARGET_KINDS, WORLDVIEW_VALUES,
  directDir, exists, fmtId, readJson, readText, writeJson,
} from "../../../common.js";
import { asList, type Dict, idNum, isDict, isList, opErr, strOf } from "../shared.js";

export function parseAssetTarget(raw: unknown): [string, string] {
  const value = strOf(raw).trim();
  if (!value.includes(":")) {
    throw new CliError("Target invalid.", {
            exitCode: EXIT_USAGE,
            errorCode: "TARGET_INVALID",
            hint: "Use a concrete asset target.",
            data: { expected: ["target: actor:<id>, location:<id>, or prop:<id>"], actual: [value || "<empty>"] },
          });
  }
  const idx = value.indexOf(":");
  const kind = value.slice(0, idx).trim();
  const targetId = value.slice(idx + 1).trim();
  if (!SCRIPT_TARGET_KINDS.has(kind) || !targetId) {
    throw new CliError("Target invalid.", {
            exitCode: EXIT_USAGE,
            errorCode: "TARGET_INVALID",
            hint: "Use actor, location, or prop with a non-empty id.",
            data: { expected: ["target: actor:<id>, location:<id>, or prop:<id>"], actual: [value] },
          });
  }
  return [kind, targetId];
}

export function parseStateTarget(raw: unknown): [string, string, string] {
  const value = strOf(raw).trim();
  if (!value.includes("/")) {
    throw new CliError("State target invalid.", {
            exitCode: EXIT_USAGE,
            errorCode: "STATE_TARGET_INVALID",
            hint: "Use a concrete state target.",
            data: { expected: ["state target: actor:<id>/<state_id>"], actual: [value || "<empty>"] },
          });
  }
  const lastSlash = value.lastIndexOf("/");
  const assetRaw = value.slice(0, lastSlash);
  const stateId = value.slice(lastSlash + 1).trim();
  const [kind, targetId] = parseAssetTarget(assetRaw);
  if (!stateId) {
    throw new CliError("State target invalid.", {
            exitCode: EXIT_USAGE,
            errorCode: "STATE_TARGET_INVALID",
            hint: "Use a non-empty state_id.",
            data: { expected: ["state_id after '/'"], actual: [value] },
          });
  }
  return [kind, targetId, stateId];
}

export function parseSceneRef(raw: unknown): [string, string] {
  const value = strOf(raw).trim();
  if (!value.includes("/")) {
    throw new CliError("Scene ref invalid.", {
            exitCode: EXIT_USAGE,
            errorCode: "SCENE_REF_INVALID",
            hint: "Use ep_id/scn_id.",
            data: { expected: ["scene ref: ep_001/scn_001"], actual: [value || "<empty>"] },
          });
  }
  const idx = value.indexOf("/");
  return [value.slice(0, idx).trim(), value.slice(idx + 1).trim()];
}

export function parseActionRef(raw: unknown): [string, string, number] {
  const value = strOf(raw).trim();
  if (!value.includes("#")) {
    throw new CliError("Action ref invalid.", {
            exitCode: EXIT_USAGE,
            errorCode: "ACTION_REF_INVALID",
            hint: "Use ep_id/scn_id#action_index.",
            data: { expected: ["action ref: ep_001/scn_001#4"], actual: [value || "<empty>"] },
          });
  }
  const lastHash = value.lastIndexOf("#");
  const sceneRaw = value.slice(0, lastHash);
  const indexRaw = value.slice(lastHash + 1);
  const [epId, sceneId] = parseSceneRef(sceneRaw);
  const actionIndex = parseInt(indexRaw, 10);
  if (Number.isNaN(actionIndex)) {
    throw new CliError("Action index invalid.", {
            exitCode: EXIT_USAGE,
            errorCode: "ACTION_INDEX_INVALID",
            hint: "Use a zero-based action index.",
            data: { expected: ["integer action index after '#'"], actual: [value] },
          });
  }
  return [epId, sceneId, actionIndex];
}

export type AnyAddress =
  | { kind: "action"; episodeId: string; sceneId: string; actionIndex: number }
  | { kind: "scene"; episodeId: string; sceneId: string }
  | { kind: "asset"; assetKind: string; assetId: string }
  | { kind: "state"; assetKind: string; assetId: string; stateId: string }
  | { kind: "episode"; episodeId: string }
  | { kind: "speaker"; speakerId: string }
  | { kind: "global" };

// Try each format in order. The order matters: action (has `#`) is checked
// before scene (just `/`), and asset+state (has `:`) is checked before scene.
// Episode and speaker are bare ids (no separators) distinguished by prefix;
// `global` is the literal script-level sentinel.
export function parseAnyAddress(raw: unknown): AnyAddress {
  const value = strOf(raw).trim();
  if (!value) {
    throw new CliError("Address empty.", {
            exitCode: EXIT_USAGE,
            errorCode: "ADDRESS_EMPTY",
            hint: "Pass an address like ep_001/scn_001#3, ep_001/scn_001, actor:act_001, actor:act_001/st_001, ep_001, spk_001, or global.",
            data: { expected: ["one of the supported address formats"], actual: ["<empty>"] },
          });
  }
  if (value.includes("#")) {
    const [episodeId, sceneId, actionIndex] = parseActionRef(value);
    return { kind: "action", episodeId, sceneId, actionIndex };
  }
  if (value.includes(":")) {
    if (value.slice(value.indexOf(":") + 1).includes("/")) {
      const [assetKind, assetId, stateId] = parseStateTarget(value);
      return { kind: "state", assetKind, assetId, stateId };
    }
    const [assetKind, assetId] = parseAssetTarget(value);
    return { kind: "asset", assetKind, assetId };
  }
  if (value.includes("/")) {
    const [episodeId, sceneId] = parseSceneRef(value);
    return { kind: "scene", episodeId, sceneId };
  }
  if (value === "global") {
    return { kind: "global" };
  }
  if (/^ep_/.test(value)) {
    return { kind: "episode", episodeId: value };
  }
  if (/^spk_/.test(value)) {
    return { kind: "speaker", speakerId: value };
  }
  throw new CliError("Address invalid.", {
          exitCode: EXIT_USAGE,
          errorCode: "ADDRESS_INVALID",
          hint: "Use a recognized address format.",
          data: { expected: [
      "address: ep_NNN/scn_NNN#idx (action) | ep_NNN/scn_NNN (scene) | actor|location|prop:id (asset) | actor|location|prop:id/state_id (state) | ep_NNN (episode) | spk_XXX (speaker) | global (script-level)",
    ], actual: [value] },
        });
}
